import { forwardRef, useCallback, useEffect, useRef } from 'react'

import { isValidExpirationDate } from '../../utils/validation'
import { ArcadeHelpTextComponentProps } from '../Arcade/Input/ArcadeHelpText'
import FormattedInput, { BaseFormattedInputProps, formatTemplateValue } from '../Arcade/Input/FormattedInput'

export interface ExpirationDateInputProps extends BaseFormattedInputProps, ArcadeHelpTextComponentProps {}

const EXPIRATION_DATE_TEMPLATE = '##/##'

const ExpirationDateInput = forwardRef<HTMLInputElement, ExpirationDateInputProps>(
  ({ value = '', onChange, placeholder, ...restProps }, ref) => {
    const prevValueRef = useRef(value)

    const formatExpDate = useCallback((currentValue: string, prevValue: string) => {
      const formatted = formatTemplateValue(currentValue, EXPIRATION_DATE_TEMPLATE)

      const isBackspace = formatted.length < prevValue.length
      // prevent formatting early and allow backspace
      if (formatted === '' || formatted === '1' || formatted === '0' || isBackspace) return formatted
      // autoformat invalid input
      if (formatted === '1/' || formatted === '0/') return '01/'

      const valueWithoutSlash = formatted.replace(/\//g, '')
      let month = valueWithoutSlash.substring(0, 2)
      let year = valueWithoutSlash.substring(2, 6)

      // Handle pasting 4-digit years and just take the last 2 digits
      if (year.length === 4) {
        year = year.substring(2, 4)
      }

      if (month.length === 1 && Number(month) > 1) {
        month = `0${Number(month)}`
      } else if (month.length === 2) {
        if (Number(month) === 0) {
          month = '01'
        } else if (Number(month) > 12) {
          month = '12'
        }
      }

      return `${month}/${year}`
    }, [])

    useEffect(() => {
      if (value !== prevValueRef.current) {
        prevValueRef.current = value
      }
    }, [value])

    return (
      <FormattedInput
        {...restProps}
        autoComplete="cc-exp"
        onChange={(newValue: string) => {
          const formattedValue = formatExpDate(newValue, prevValueRef.current)
          prevValueRef.current = formattedValue

          if (formattedValue !== value) {
            onChange(formattedValue)
          }
        }}
        placeholder={placeholder || 'MM/YY'}
        ref={ref}
        template={EXPIRATION_DATE_TEMPLATE}
        value={value}
        applyCustomFormat={(inputValue: string) => formatExpDate(inputValue, prevValueRef.current)}
        onPaste={e => {
          // clipboardData is not available in all browsers (??)
          const clipboardData = e?.clipboardData?.getData('text')

          if (clipboardData == null) {
            return
          }

          if (!isValidExpirationDate(clipboardData)) {
            e.stopPropagation()
            e.preventDefault()
          }
        }}
      />
    )
  }
)

ExpirationDateInput.displayName = 'ExpirationDateInput'

export default ExpirationDateInput
